import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Fab,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close,
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Phone,
  Email,
  WhatsApp,
  Business,
  Schedule,
  LocationOn,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import ContatoFormModal from './ContatoFormModal';

interface Contato {
  id: number;
  tipo: 'canal_atendimento' | 'escritorios' | 'departamentos';
  tipocanal: 'email' | 'whatsapp' | 'telefone';
  numero?: string;
  email?: string;
  horario_atendimento?: string;
  endereco?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  coordenadas?: string;
  descricao?: string;
  status: 'ativo' | 'inativo';
  ordem: number;
  createdAt: string;
  updatedAt: string;
}

interface ContatosModalProps {
  open: boolean;
  onClose: () => void;
  tipo: 'canal_atendimento' | 'escritorios' | 'departamentos';
}

const ContatosModal: React.FC<ContatosModalProps> = ({ open, onClose, tipo }) => {
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para o formulário
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [contatoParaEditar, setContatoParaEditar] = useState<Contato | null>(null);

  const getTipoLabel = (tipo: string) => {
    const labels = {
      canal_atendimento: 'Canais de Atendimento',
      escritorios: 'Escritórios',
      departamentos: 'Departamentos'
    };
    return labels[tipo as keyof typeof labels] || tipo;
  };

  const getTipocanalIcon = (tipocanal: string) => {
    switch (tipocanal) {
      case 'telefone': return <Phone fontSize="small" />;
      case 'email': return <Email fontSize="small" />;
      case 'whatsapp': return <WhatsApp fontSize="small" />;
      default: return <Phone fontSize="small" />;
    }
  };

  const getTipocanalColor = (tipocanal: string) => {
    switch (tipocanal) {
      case 'telefone': return 'primary';
      case 'email': return 'info';
      case 'whatsapp': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'ativo' ? 'success' : 'error';
  };

  const carregarContatos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('sysup_token');
      if (!token) {
        setError('Token de autenticação não encontrado');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/appcliente-notificacoes/contatos/tipo/${tipo}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setContatos(data.data.contatos);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Erro ao carregar contatos');
      }
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
      setError('Erro ao carregar contatos');
    } finally {
      setLoading(false);
    }
  };

  const handleAlterarStatus = async (id: number, novoStatus: 'ativo' | 'inativo') => {
    try {
      const token = localStorage.getItem('sysup_token');
      if (!token) {
        toast.error('Token de autenticação não encontrado');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/appcliente-notificacoes/contatos/${id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ status: novoStatus }),
        }
      );

      if (response.ok) {
        toast.success(`Status alterado para ${novoStatus}`);
        carregarContatos(); // Recarregar lista
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Erro ao alterar status');
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status');
    }
  };

  // Funções para o formulário
  const handleOpenFormModal = (contato?: Contato) => {
    setContatoParaEditar(contato || null);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setContatoParaEditar(null);
  };

  const handleSaveContato = async (dadosContato: any) => {
    try {
      const token = localStorage.getItem('sysup_token');
      if (!token) {
        toast.error('Token de autenticação não encontrado');
        return;
      }

      const url = dadosContato.id
        ? `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/appcliente-notificacoes/contatos/${dadosContato.id}`
        : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/appcliente-notificacoes/contatos`;

      const method = dadosContato.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(dadosContato),
      });

      if (response.ok) {
        toast.success(`Contato ${dadosContato.id ? 'atualizado' : 'criado'} com sucesso!`);
        carregarContatos(); // Recarregar lista
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Erro ao salvar contato');
        throw new Error(errorData.message || 'Erro ao salvar contato');
      }
    } catch (error) {
      console.error('Erro ao salvar contato:', error);
      throw error;
    }
  };

  const handleExcluirContato = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este contato?')) {
      return;
    }

    try {
      const token = localStorage.getItem('sysup_token');
      if (!token) {
        toast.error('Token de autenticação não encontrado');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/appcliente-notificacoes/contatos/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Contato excluído com sucesso!');
        carregarContatos(); // Recarregar lista
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Erro ao excluir contato');
      }
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
      toast.error('Erro ao excluir contato');
    }
  };

  useEffect(() => {
    if (open) {
      carregarContatos();
    }
  }, [open, tipo]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business color="primary" />
          Gerenciar {getTipoLabel(tipo)}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
                               <TableHead>
                   <TableRow>
                     <TableCell><strong>Ordem</strong></TableCell>
                     <TableCell><strong>Canal</strong></TableCell>
                     <TableCell><strong>Contato</strong></TableCell>
                     <TableCell><strong>Horário</strong></TableCell>
                     <TableCell><strong>Endereço</strong></TableCell>
                     <TableCell><strong>Descrição</strong></TableCell>
                     <TableCell><strong>Status</strong></TableCell>
                     <TableCell><strong>Ações</strong></TableCell>
                   </TableRow>
                 </TableHead>
              <TableBody>
                {contatos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhum contato encontrado para {getTipoLabel(tipo).toLowerCase()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contatos.map((contato) => (
                    <TableRow key={contato.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {contato.ordem}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTipocanalIcon(contato.tipocanal)}
                          label={contato.tipocanal}
                          size="small"
                          color={getTipocanalColor(contato.tipocanal)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          {contato.tipocanal === 'email' && contato.email && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Email fontSize="small" />
                              {contato.email}
                            </Typography>
                          )}
                          {(contato.tipocanal === 'telefone' || contato.tipocanal === 'whatsapp') && contato.numero && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {contato.tipocanal === 'whatsapp' ? <WhatsApp fontSize="small" /> : <Phone fontSize="small" />}
                              {contato.numero}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {contato.horario_atendimento ? (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Schedule fontSize="small" />
                            {contato.horario_atendimento}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Não informado
                          </Typography>
                        )}
                      </TableCell>
                                             <TableCell>
                         {contato.endereco ? (
                           <Box>
                             <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                               <LocationOn fontSize="small" />
                               {contato.endereco}
                             </Typography>
                             {contato.bairro && (
                               <Typography variant="caption" color="text.secondary">
                                 {contato.bairro}, {contato.cidade} - {contato.estado}
                               </Typography>
                             )}
                           </Box>
                         ) : (
                           <Typography variant="body2" color="text.secondary">
                             Não informado
                           </Typography>
                         )}
                       </TableCell>
                       <TableCell>
                         {contato.descricao ? (
                           <Typography variant="body2" sx={{ maxWidth: 200 }}>
                             {contato.descricao.length > 100 
                               ? `${contato.descricao.substring(0, 100)}...` 
                               : contato.descricao
                             }
                           </Typography>
                         ) : (
                           <Typography variant="body2" color="text.secondary">
                             Não informado
                           </Typography>
                         )}
                       </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={contato.status === 'ativo'}
                              onChange={(e) => handleAlterarStatus(
                                contato.id, 
                                e.target.checked ? 'ativo' : 'inativo'
                              )}
                              size="small"
                            />
                          }
                          label={
                            <Chip
                              label={contato.status}
                              size="small"
                              color={getStatusColor(contato.status)}
                              variant="outlined"
                            />
                          }
                          labelPlacement="end"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenFormModal(contato)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleExcluirContato(contato.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} startIcon={<Close />}>
          Fechar
        </Button>
        
        <Tooltip title="Adicionar novo contato">
          <Fab
            color="primary"
            size="small"
            sx={{ position: 'absolute', bottom: 16, right: 16 }}
            onClick={() => handleOpenFormModal()}
          >
            <Add />
          </Fab>
        </Tooltip>
      </DialogActions>

      {/* Modal do Formulário */}
      <ContatoFormModal
        open={formModalOpen}
        onClose={handleCloseFormModal}
        tipo={tipo}
        contato={contatoParaEditar}
        onSave={handleSaveContato}
      />
    </Dialog>
  );
};

export default ContatosModal;
